import * as PIXI from 'pixi.js';
import { ANIMATION, AUDIO, IMAGE, TEXT, VIDEO, WATERMARK } from './MediaTypes';
import {
  AnimationMedia,
  AudioMedia,
  ImageMedia,
  TextMedia,
  VideoMedia,
} from './';
import NativeMediaControls from '../utils/NativeMediaControls';
import Color from '../utils/Color';
import {DEFAULT_LAYER_ZINDEX, FOREGROUND_IMAGE_ZINDEX} from '../constants/Layers';
import Container from '../utils/Container';
import logger from '../utils/logger';

export default class MediaItem extends Container {
  /**
   * Create a MediaItem from a storyboard model.
   */
  static async createFromModel(model, app) {
    switch (model.mediaType) {
      case ANIMATION: {
        return new AnimationMedia(model, app);
      }
      case AUDIO: {
        const audio = await NativeMediaControls.preloadOffscreenAudio(
          model.source
        );
        return new AudioMedia(model, app, audio);
      }
      case WATERMARK:
      case IMAGE: {
        const image = await NativeMediaControls.preloadOffscreenImage(
          model.source
        );
        return new ImageMedia(model, app, image);
      }
      case TEXT: {
        return new TextMedia(model, app);
      }
      case VIDEO: {
        const video = await NativeMediaControls.preloadOffscreenVideo(
          model.source,
          model.volume
        );
        return new VideoMedia(model, app, video);
      }
      default:
        return null;
    }
  }

  _destroyed = false;

  active = true;

  constructor(app, hasContainer = false) {
    super(app);
    this.app = app;

    if (hasContainer) {
      this.container = new PIXI.Container();
      this.displayObject = this.container;
      if (!this.displayObject.mask) {
        this.displayObject.mask = new PIXI.Graphics();
      }
    } else {
      this.container = null;
    }
    this.model = {};
  }

  get greensockTimeline() {
    return null;
  }

  play() {}

  pause() {}

  mute() {}

  unmute() {}

  setPlaybackVolume() {}

  seek() {
    return Promise.resolve();
  }

  render(play = false) {
    this.active = true;
    if (play) {
      this.play();
    }
  }

  deactivate() {
    if (!this.active) {
      return;
    }

    this.active = false;
    this.pause();
    this.seek(0);
  }

  destroy() {
    this.pause();
    this._destroyed = true;
    if (this.container) {
      try {
        this.container.destroy();
      } catch (error) {
        logger.warn('Error destroying MediaItem container', { error });
      }
    }
  }

  get id() {
    return this.model.id;
  }

  getDefaultZIndex() {
    return FOREGROUND_IMAGE_ZINDEX;
  }

  updateModel(model) {
    this.model = model;
    this.end = model.startTime + model.duration;
    // ignore the parts of the item that are before time 0 on the timeline
    this.start = Math.max(model.startTime, 0);
    this.duration = this.end - this.start;
    if (this.container) {
      this.container.zIndex = model.zIndex || this.getDefaultZIndex();
      this._drawMask();
    }
  }

  get destroyed() {
    return this._destroyed;
  }

  _drawMask() {
    // Force masking of all visual media items to prevent out of bounds draws
    this.displayObject.mask
      .clear()
      .beginFill(Color.toNumber(Color.WHITE))
      .drawRect(0, 0, this.app.screen.width, this.app.screen.height)
      .endFill();
  }
}
